import React, { useEffect, useRef, useState } from 'react';
import {
    BadgeEuro,
    BarChart3,
    Boxes,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ClipboardList,
    DatabaseZap,
    FileText,
    Home,
    MapPinned,
    PackageCheck,
    RefreshCw,
    ShieldCheck,
    Store,
    Table2,
    TrendingUp,
    UserCog,
    Users
} from 'lucide-react';

const Sidebar = ({
    currentView,
    setCurrentView,
    warehouseOperationsMode = 'operative',
    setWarehouseOperationsMode,
    isSidebarOpen,
    setIsSidebarOpen,
    isDarkMode,
    isAdmin,
    availableTenants = [],
    activeTenantId = '',
    activeTenantName = 'Shop',
    activeTenantRole = 'member',
    isActiveDemoTenant = false,
    onTenantChange
}) => {
    const [isShippingExpanded, setIsShippingExpanded] = useState(currentView === 'warehouseOperations');
    const hoverCloseTimer = useRef(null);
    useEffect(() => () => {
        if (hoverCloseTimer.current) window.clearTimeout(hoverCloseTimer.current);
    }, []);
    const openSidebar = () => {
        if (hoverCloseTimer.current) window.clearTimeout(hoverCloseTimer.current);
        hoverCloseTimer.current = null;
        setIsSidebarOpen(true);
    };
    const closeSidebarAfterHover = () => {
        if (hoverCloseTimer.current) window.clearTimeout(hoverCloseTimer.current);
        hoverCloseTimer.current = window.setTimeout(() => {
            setIsSidebarOpen(false);
            hoverCloseTimer.current = null;
        }, 110);
    };
    const bgCard = isDarkMode ? 'bg-[#292929]' : 'bg-white';
    const borderCard = isDarkMode ? 'border-[#404040]' : 'border-slate-200';
    const hasTenants = availableTenants.length > 0;
    const canSwitchTenants = availableTenants.length > 1 && typeof onTenantChange === 'function';
    const showTenantBadge = activeTenantRole === 'viewer' && !isActiveDemoTenant;
    const tenantBadgeLabel = 'Nur lesen';
    const tenantDisplayName = isActiveDemoTenant ? 'Demo Shop' : activeTenantName;
    const tenantBadgeTitle = activeTenantRole === 'viewer'
        ? 'Demo oder schreibgeschützter Mandant'
        : 'Demo-Mandant';
    const tenantOptionStyle = {
        backgroundColor: isDarkMode ? '#292929' : '#ffffff',
        color: isDarkMode ? '#f1f5f9' : '#172033'
    };

    const activeNavLinkClass = 'bg-[#e85b17] text-white ring-1 ring-[#ff9a5b]/60 shadow-[0_10px_24px_rgba(232,91,23,0.28)]';

    const inactiveNavLinkClass = isDarkMode
        ? 'text-[#e0e0e0] hover:bg-[#404040] hover:text-white'
        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950';
    const tenantPanelClass = isDarkMode ? 'border-[#404040] text-slate-100' : 'border-slate-200 bg-slate-50 text-slate-700';
    const tenantSelectClass = isDarkMode ? 'border-[#404040] bg-[#292929] text-slate-100 focus:border-[#E55C1A]' : 'border-slate-200 bg-white text-slate-800 focus:border-orange-500';
    const tenantBadgeClass = isDarkMode ? 'border-[#E55C1A]/35 bg-[#E55C1A]/15 text-[#ff8d4a]' : 'border-orange-200 bg-orange-50 text-orange-700';

    const navGroups = [
        {
            id: 'cockpit',
            label: 'Cockpit',
            items: [
                { id: 'overview', label: 'Dashboard', Icon: Home },
                { id: 'kpiWidgets', label: 'KPI Widgets', Icon: BarChart3 },
                { id: 'comparison', label: 'Profit', Icon: TrendingUp },
                { id: 'controllingReports', label: 'Controlling', Icon: Table2 }
            ]
        },
        {
            id: 'operations',
            label: 'Operatives Geschäft',
            items: [
                { id: 'articles', label: 'Produkte', Icon: PackageCheck },
                { id: 'customers', label: 'Kunden', Icon: Users },
                { id: 'warehouse', label: 'Lager', Icon: Boxes },
                { id: 'warehouseOperations', label: 'Versand', Icon: ClipboardList },
                { id: 'returns', label: 'Retouren', Icon: RefreshCw }
            ]
        },
        {
            id: 'management',
            label: 'Steuerung',
            items: [
                { id: 'map', label: 'Regionen', Icon: MapPinned },
                { id: 'costs', label: 'Kosten', Icon: BadgeEuro },
                { id: 'dataQuality', label: 'Datenqualität', Icon: DatabaseZap },
                { id: 'reports', label: 'Reports', Icon: FileText }
            ]
        }
    ];

    const renderNavItem = (item, secondary = false) => {
        const Icon = item.Icon;
        return (
            <button
                type="button"
                key={item.id}
                onClick={() => setCurrentView(item.id)}
                className={`dashboard-sidebar-nav-item flex w-full items-center justify-start gap-2 rounded-lg px-2.5 py-1.5 text-[13px] ${secondary ? 'font-medium' : 'font-semibold'} cursor-pointer ${currentView === item.id ? activeNavLinkClass : inactiveNavLinkClass}`}
                title={item.label}
            >
                <Icon className="dashboard-sidebar-nav-icon h-[18px] w-[18px] shrink-0" strokeWidth={2.2} />
                <span className={`dashboard-sidebar-label truncate ${isSidebarOpen ? 'dashboard-sidebar-label-visible' : ''}`}>{item.label}</span>
            </button>
        );
    };

    const renderShippingNavItem = (item, secondary = false) => {
        const Icon = item.Icon;
        const isActive = currentView === 'warehouseOperations';
        const selectShippingMode = (mode) => {
            setCurrentView('warehouseOperations');
            if (typeof setWarehouseOperationsMode === 'function') {
                setWarehouseOperationsMode(mode);
            }
            setIsShippingExpanded(true);
        };

        const onMainClick = () => {
            if (!isSidebarOpen) {
                selectShippingMode(['operative', 'analysis', 'billing'].includes(warehouseOperationsMode) ? warehouseOperationsMode : 'operative');
                setIsSidebarOpen(true);
                return;
            }
            setIsShippingExpanded((expanded) => !expanded);
        };

        return (
            <div key={item.id} className="space-y-0.5">
                <button
                    type="button"
                    onClick={onMainClick}
                    className={
                        'dashboard-sidebar-nav-item dashboard-sidebar-shipping-nav-item grid w-full items-center rounded-lg px-2.5 py-1.5 text-[13px] ' + (
                            secondary ? 'font-medium ' : 'font-semibold '
                        ) + 'cursor-pointer transition-all ' + (
                            isSidebarOpen ? 'justify-start ' : 'justify-center '
                        ) + (isActive ? activeNavLinkClass : inactiveNavLinkClass)
                    }
                    title={'Versand · ' + ({ operative: 'Operativ', analysis: 'Analyse', billing: 'Abrechnungen' }[warehouseOperationsMode] || 'Operativ')}
                    aria-expanded={isSidebarOpen ? isShippingExpanded : undefined}
                >
                    <Icon className="dashboard-sidebar-shipping-icon dashboard-sidebar-nav-icon h-[18px] w-[18px] shrink-0" strokeWidth={2.2} />
                    <span className={`dashboard-sidebar-label min-w-0 truncate text-left ${isSidebarOpen ? 'dashboard-sidebar-label-visible' : ''}`}>Versand</span>
                    <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-all duration-300 ${isShippingExpanded ? 'rotate-180' : ''} ${isSidebarOpen ? 'opacity-100' : 'w-0 opacity-0'}`} strokeWidth={2.4} />
                </button>

                {isShippingExpanded && (
                    <div className={`space-y-0.5 ${isSidebarOpen ? `ml-4 border-l pl-2 ${isDarkMode ? 'border-white/10' : 'border-slate-200'}` : ''}`}>
                        {[
                            ['operative', 'Operativ', ClipboardList],
                            ['analysis', 'Analyse', BarChart3],
                            ['billing', 'Abrechnungen', FileText]
                        ].map(([mode, label, ModeIcon]) => {
                            const active = isActive && warehouseOperationsMode === mode;
                            return (
                                <button
                                    key={mode}
                                    type="button"
                                    onClick={() => selectShippingMode(mode)}
                                    title={label}
                                    aria-label={`Versand ${label}`}
                                    className={
                                        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-semibold transition ' + (
                                            isSidebarOpen ? 'justify-start ' : 'justify-center '
                                        ) + (
                                            active
                                                ? isDarkMode ? 'bg-[#E55C1A]/15 text-orange-200' : 'bg-orange-100 text-orange-700'
                                                : isDarkMode ? 'text-slate-400 hover:bg-white/[0.05] hover:text-slate-200' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                                        )
                                    }
                                >
                                    <ModeIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.2} />
                                    <span className={`dashboard-sidebar-label ${isSidebarOpen ? 'dashboard-sidebar-label-visible' : ''}`}>{label}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };
    return (
        <div className="dashboard-sidebar-rail relative z-50 h-full w-16 shrink-0">
        <aside
            className={`dashboard-sidebar ${isSidebarOpen ? 'dashboard-sidebar-open w-56' : 'dashboard-sidebar-collapsed w-16'} ${bgCard} border-r ${borderCard} absolute inset-y-0 left-0 z-50 flex flex-col gap-2.5 p-2.5`}
            onMouseEnter={openSidebar}
            onMouseLeave={closeSidebarAfterHover}
        >
            <button
                type="button"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className={`dashboard-sidebar-toggle absolute -right-4 top-8 z-[60] flex h-8 w-8 items-center justify-center rounded-full border shadow-md transition-colors ${isDarkMode ? 'bg-[#292929] border-[#404040] text-gray-400 hover:text-white' : 'bg-white border-slate-200 text-slate-500 hover:text-orange-600'}`}
                aria-label={isSidebarOpen ? 'Navigation einklappen' : 'Navigation ausklappen'}
                title={isSidebarOpen ? 'Navigation einklappen' : 'Navigation ausklappen'}
            >
                <ChevronRight className={`h-4 w-4 transition-transform duration-300 ${isSidebarOpen ? 'rotate-180' : ''}`} strokeWidth={2.5} />
            </button>

            {/* LOGO */}
            <div className={`dashboard-sidebar-logo flex h-10 shrink-0 items-center justify-center overflow-hidden border-b pb-2 transition-all duration-300 ${borderCard}`}>
                <img src={isDarkMode ? '/commetriq-logo-dark.webp' : '/commetriq-logo-light.webp'} alt="commetriq Logo" className={`dashboard-sidebar-logo-image-open h-6 w-auto max-w-[150px] object-contain transition-opacity duration-200 ${isSidebarOpen ? 'opacity-100' : 'opacity-0'}`} />
                <span aria-hidden="true" className={`dashboard-sidebar-logo-image-closed h-7 w-7 overflow-hidden transition-opacity duration-200 ${isSidebarOpen ? 'opacity-0' : 'opacity-100'}`}>
                    <img src={isDarkMode ? '/commetriq-logo-dark.webp' : '/commetriq-logo-light.webp'} alt="" className="h-7 w-auto max-w-none" />
                </span>
            </div>
            <nav className="dashboard-sidebar-nav sidebar-nav-scroll-area flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto pr-1">
                {navGroups.map((group, groupIndex) => (
                    <div
                        key={group.id}
                        className={groupIndex > 0 ? ['dashboard-sidebar-nav-group dashboard-sidebar-nav-group-separated flex flex-col gap-0.5 border-t pt-2', borderCard].join(' ') : 'dashboard-sidebar-nav-group flex flex-col gap-0.5'}
                    >
                        <p className={`dashboard-sidebar-section-label dashboard-sidebar-nav-label px-2 text-[9px] font-black uppercase leading-4 tracking-wider ${isSidebarOpen ? 'dashboard-sidebar-section-label-visible' : ''} ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            {group.label}
                        </p>
                        {group.items.map(item => (
                            item.id === 'warehouseOperations'
                                ? renderShippingNavItem(item, groupIndex > 0)
                                : renderNavItem(item, groupIndex > 0)
                        ))}
                    </div>
                ))}
            </nav>

            <div className={`dashboard-sidebar-footer flex shrink-0 flex-col gap-2 border-t pt-2 ${borderCard}`}>
                {hasTenants && (
                    <div className={`dashboard-sidebar-tenant-slot h-[56px] shrink-0 ${isSidebarOpen ? '' : 'flex items-center justify-center'}`}>
                        {isSidebarOpen ? (
                            <div className="space-y-1.5">
                                <div className="flex items-center gap-2 px-1">
                                    <div className="flex min-w-0 items-center gap-2">
                                        <Store className={`h-3.5 w-3.5 shrink-0 ${isDarkMode ? 'text-[#E55C1A]' : 'text-orange-600'}`} strokeWidth={2.2} />
                                        <span className={`truncate text-[10px] font-black uppercase tracking-wide ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                            Shop
                                        </span>
                                    </div>
                                </div>

                                {canSwitchTenants ? (
                                    <div className={`flex h-8 w-full items-center gap-1.5 rounded-lg border px-2 transition-colors ${tenantSelectClass}`}>
                                        <select
                                            value={activeTenantId}
                                            onChange={(event) => onTenantChange(event.target.value)}
                                            className={`h-full min-w-0 flex-1 bg-transparent text-xs font-semibold outline-none ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}
                                            style={{ colorScheme: isDarkMode ? 'dark' : 'light' }}
                                            title={tenantDisplayName}
                                        >
                                            {availableTenants.map(tenant => (
                                                <option key={tenant.id} value={tenant.id} style={tenantOptionStyle}>
                                                {tenant.is_demo ? 'Demo Shop' : tenant.name}
                                            </option>
                                        ))}
                                    </select>
                                        {showTenantBadge && (
                                            <span
                                                className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[9px] font-semibold tracking-normal ${tenantBadgeClass}`}
                                                title={tenantBadgeTitle}
                                            >
                                                {tenantBadgeLabel}
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        className={`flex h-8 w-full items-center gap-1.5 rounded-lg border px-2 text-xs font-semibold ${tenantSelectClass}`}
                                        title={activeTenantName}
                                    >
                                        <span className="min-w-0 flex-1 truncate">{tenantDisplayName}</span>
                                        {showTenantBadge && (
                                            <span
                                                className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[9px] font-semibold tracking-normal ${tenantBadgeClass}`}
                                                title={tenantBadgeTitle}
                                            >
                                                {tenantBadgeLabel}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={openSidebar}
                                className={`relative flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${tenantPanelClass}`}
                                title={`Shop: ${tenantDisplayName}`}
                                aria-label="Shop-Auswahl öffnen"
                            >
                                <Store className={`h-4 w-4 ${isDarkMode ? 'text-[#E55C1A]' : 'text-orange-600'}`} strokeWidth={2.3} />
                                {showTenantBadge && (
                                    <span className={`absolute right-1 top-1 h-2 w-2 rounded-full bg-emerald-500 ring-2 ${isDarkMode ? 'ring-[#292929]' : 'ring-white'}`} />
                                )}
                            </button>
                        )}
                    </div>
                )}

                <div className="space-y-1.5">
                    {renderNavItem({ id: 'profile', label: 'Profil', Icon: UserCog }, true)}
                    {isAdmin && (
                        renderNavItem({ id: 'admin', label: 'Admin-Center', Icon: ShieldCheck }, true)
                    )}
                </div>
            </div>
        </aside>
        </div>
    );
};

export default Sidebar;
